<script lang="ts">
	import type { Material } from './types/material'
	import { DefaultMaterial } from './types/material'
	import MaterialDetails from './MaterialDetails.svelte'
	import TreeView from '#lib/organisms/TreeView.svelte'
	import Tooltip from '#lib/atoms/Tooltip.svelte'
	import type { Catalog } from './types/catalog'
	import { modals } from 'svelte-modals'
	import Modal from '#lib/organisms/Modal.svelte'
	import { addNotification } from '#lib/stores.js'
	import type { Notification } from '$types/notification'
	import { DefaultNotification, NotificationType } from '$types/notification'
	import { onMount, untrack } from 'svelte'
	import { t } from '$i18n/i18n'
	import { changeUrl } from '$utils/misc'
	import type { Filter } from '$utils/filter'
	import { applyFilters } from '$utils/filter'
	import { deepEqual } from '$utils/utils.svelte'
	import type { MaterialProperty } from './types/materialProperty'
	import { TooltipPlacements } from '$types/enums'
	import { getParentProperties } from './util/materialUtil'
	import Button from '#lib/atoms/Button.svelte'
	import IconPlus from '$svg/icon-plus.svg?component'
	import { mmpvState } from '#lib/mmpv/mmpv.state.svelte.js'
	import { getSortedPropertiesOfMaterial } from './mmpv-properties.controller.svelte'

	type MaterialDetailsEditViewProps = {
		backToCatalogs?: () => void
	}

	let { backToCatalogs }: MaterialDetailsEditViewProps = $props()

	let catalog: Catalog = $derived(mmpvState.selectedCatalog)
	let material: Material = $derived(mmpvState.selectedMaterial)

	let properties: Map<string, MaterialProperty> = $derived(mmpvState.materialProperties)
	// copy to check for differences -> if different, we need to save changes.
	// Must be initialized before render: bind: to undefined throws
	// props_invalid_value since the Svelte 5 child declares a $bindable fallback
	let materialCopy: Material = $state({ ...material })
	let isNew: boolean = $state(false)
	const filters: Filter[] = $derived(mmpvState.filters)

	onMount(() => {
		if (!catalog) return
		if (!Object.hasOwn(catalog, 'nodes') || catalog.nodes.length === 0) {
			addRoot()
		}
		// insert labels and trigger update
		catalog.nodes.forEach(
			(node: Material) =>
				(node.label =
					(node?.parentCode ?? '') +
					node.code +
					(node.highlighted ? catalog.highlightedValue : '') +
					' ' +
					node.name)
		)
		// $state.snapshot deproxifies before structuredClone, which cannot clone $state proxies
		materialCopy = $state.snapshot(material)
		filterNodesAndSetSelectedMaterial()
	})

	function changeMaterial(selectedMaterial: Material) {
		if (!selectedMaterial) {
			selectedMaterial = DefaultMaterial('root', null, 0)
		}
		addPropertiesToSelectedMaterial(selectedMaterial)
		const plainMaterial = $state.snapshot(selectedMaterial)
		// material is a read-only $derived(mmpvState.selectedMaterial); write to the source
		mmpvState.selectedMaterial = plainMaterial
		materialCopy = plainMaterial
		showEditProperty = false // we do not want to see the property edit window
		changeUrl(`catalog/${catalog.name}/material/${material.id}`, window)
	}

	function addPropertiesToSelectedMaterial(selectedMaterial: Material) {
		selectedMaterial.properties = [] // needed => doesn't exist if empty from server
		if (selectedMaterial.propertyIds && selectedMaterial.propertyIds.length > 0) {
			selectedMaterial.propertyIds.forEach((propertyId: string) => {
				const materialProperty = properties.get(propertyId)
				if (!materialProperty) {
					return
				}
				// clone before toggling isEditable so we never mutate the shared map entry
				selectedMaterial.properties.push({ ...materialProperty, isEditable: true })
			})
		}
		let hereditaryPropertyIds = getParentProperties(selectedMaterial, catalog)
		hereditaryPropertyIds.forEach((propertyId) => {
			const materialProperty = properties.get(propertyId)
			if (!materialProperty) {
				return
			}
			// separate clone for hereditary properties, likewise to protect the shared map entry
			selectedMaterial.properties.push({ ...materialProperty, isEditable: false })
		})
	}

	function addNode(node: Material) {
		// not yet sent to backend
		let existingNewNode: boolean = catalog.nodes.some((n) => n.id === 'new')
		if (!existingNewNode) {
			// if there is not already a new node wih this parent
			changeMaterial(DefaultMaterial(node.id, (node.parentCode ?? '') + node.code, node.level + 1))
			isNew = true
			// if children property does not exist, we create it
			if (Object.hasOwn(node, 'childrenIds') && Array.isArray(node.childrenIds)) {
				node.childrenIds.push(material.id)
			} else {
				node.childrenIds = [material.id]
			}
			// by default expand the parent — immutable SvelteMap update so legacy TreeView
			// (which now reads via .get) re-evaluates node expansion (see TreeView.toggleExpansion)
			mmpvState.expandTreeNode(catalog.name, node.id)
			catalog.nodes.push($state.snapshot(material)) // push plain snapshot so filteredNodes contains plain objects
			filterNodesAndSetSelectedMaterial(true)
		} else {
			let notification: Notification = DefaultNotification(
				'Es existiert bereits ein neuer Knoten. Schließe erst das Erstellen dieses Knotens ab.',
				NotificationType.INFO,
				false,
				5000
			)
			addNotification(notification)
		}
	}

	// export to be able to call it from parent
	export function addRoot() {
		// not yet sent to backend
		let existingNewNode: boolean = catalog.nodes.some((node: Material) => node.id === 'new') // is there already a new node?
		if (!existingNewNode) {
			// If there is not already a new root node
			// we give it to the parent id 'root' to go through validation correctly (null is not allowed)
			changeMaterial(DefaultMaterial('root', null, 0)) // has no parent and thus no parentCode
			isNew = true
			catalog.nodes.push(material)
			filterNodesAndSetSelectedMaterial(true) // give the material such that we do not change away from the new material
		} else {
			let notification: Notification = DefaultNotification(
				'Es existiert bereits ein neuer Knoten. Schließe erst das Erstellen dieses Knotens ab.',
				NotificationType.INFO,
				false,
				5000
			)
			addNotification(notification)
			changeMaterial(DefaultMaterial('root', null, 0)) // has no parent and thus no parentCode
		}
	}

	function navigateMaterial(selectedMaterial: Material) {
		changeMaterial(selectedMaterial)
		isNew = material.id === 'new'
	}

	function handleSelectMaterial(selectedMaterial: Material) {
		// compare order-insensitive clones so the dirty-check does not reorder the
		// live material / copy as a side effect of the comparison
		const sortedMaterial = { ...material, properties: getSortedPropertiesOfMaterial(material) }
		const sortedMaterialCopy = {
			...materialCopy,
			properties: getSortedPropertiesOfMaterial(materialCopy)
		}
		let isMaterialUnedited: boolean = deepEqual(sortedMaterial, sortedMaterialCopy)
		if (isMaterialUnedited) {
			navigateMaterial(selectedMaterial)
		} else {
			modals.open(Modal, {
				title: $t('UI.modal.unsavedChanges.header'),
				message: $t('UI.mmpv.modal.material.unsavedChanges.message'),
				denyText: $t('UI.saveAndDiscardChanges'),
				onDeny: () => {
					navigateMaterial(selectedMaterial)
					modals.close()
				}
			})
		}
	}

	function onRemoveNode(nodeToRemove: Material) {
		const idx = catalog.nodes.map((node: Material) => node.id).indexOf(nodeToRemove.id)
		if (idx > -1) {
			catalog.nodes.splice(idx, 1)
			filterNodesAndSetSelectedMaterial()
			isNew = false // as we change to existing material, we do not have a new node anymore
		}
	}

	function filterNodesAndSetSelectedMaterial(keepMaterial: boolean = false) {
		const seen = new Set<string>() // eslint-disable-line svelte/prefer-svelte-reactivity
		const collectedNodes: Material[] = []
		const nodes = catalog.nodes.filter(
			(node: Material) => applyFilters(filters, node) || node.id === 'new'
		) // FIXME also show parents until root of filtered nodes
		nodes.forEach((node: Material) => {
			if (!seen.has(node.id)) {
				seen.add(node.id)
				collectedNodes.push(node)
			}
			addParentsToCollection(node, collectedNodes, seen)
		})
		// single wholesale assignment: filteredNodes is $state.raw, see its declaration
		filteredNodes = collectedNodes
		if (filters.length > 0) {
			// when we reset the filters by creating a new node, we don't want to change material
			if (!keepMaterial && filteredNodes.length > 0) {
				changeMaterial(filteredNodes[0])
			}

			// immutable SvelteMap update: build the array first so legacy TreeView re-renders
			mmpvState.treeExpansionStates.set(
				catalog.name,
				filteredNodes.map((node: Material) => node.id)
			)
		}
	}

	function addParentsToCollection(node: Material, collection: Material[], seen: Set<string>) {
		const parent = mmpvState.findMaterial(node.parentId)
		if (parent && !seen.has(parent.id)) {
			seen.add(parent.id)
			collection.push(parent)
			addParentsToCollection(parent, collection, seen)
		}
	}

	let showEditProperty: boolean = $state(false)
	/**
	 * $state.raw, NOT $state: the legacy TreeView must receive PLAIN node objects
	 * (the same references as $catalog.nodes) and react via wholesale array
	 * reassignment — exactly the pre-migration Svelte 4 semantics. A deep $state
	 * proxy breaks it: every rebuild creates a fresh proxy tree over the same
	 * underlying objects, so TreeView's legacy template effects stay subscribed
	 * to the previous proxy's signals and its DOM silently stops updating
	 * (observed: added child node never rendered despite correct state).
	 */
	let filteredNodes: Material[] = $state.raw([])

	$effect(() => {
		if (filters) {
			/**
			 * untrack: legacy `$:` only depended on `filters`. The function also reads
			 * `filteredNodes`, which it reassigns itself — tracking those reads would
			 * re-fire the effect on its own writes (effect_update_depth_exceeded).
			 */
			untrack(() => filterNodesAndSetSelectedMaterial())
		}
	})
</script>

<div class="material-tree-edit-view">
	<div>
		<TreeView
			nodes={filteredNodes}
			treeExpansionMapKey={catalog.name}
			activeNode={material}
			expansionStates={mmpvState.treeExpansionStates}
			onLabelClick={(selectedMaterial) => handleSelectMaterial(selectedMaterial as Material)}
		>
			{#snippet nodeAction(item)}
				<div class="add-node-action" style="position: relative">
					<!-- if we are hovering, we do not want the action for a leaf -->
					{#if item.level < (catalog.maxDepth ?? 0) - 1}
						<Tooltip msg={$t('UI.mmpv.addChild')} placementTtip={TooltipPlacements.RIGHT}>
							<Button
								variant="secondary-icon"
								class="add-icon"
								style="--btn-border-width: 0"
								title={$t('UI.mmpv.addChild')}
								onclick={() => addNode(item as Material)}
							>
								<IconPlus width={20} height={20} fill="currentColor" />
							</Button>
						</Tooltip>
					{/if}
				</div>
			{/snippet}
		</TreeView>
	</div>
	<div>
		<MaterialDetails
			{isNew}
			bind:showEditProperty
			bind:materialCopy
			removeNode={onRemoveNode}
			backToCatalogs={() => backToCatalogs?.()}
			{addRoot}
			changeMaterial={(material) => {
				changeMaterial(material)
				filterNodesAndSetSelectedMaterial(true)
				isNew = false
			}}
		/>
	</div>
</div>

<style>
	@import '$styles/global.scss';

	.material-tree-edit-view {
		display: grid;
		grid-template-columns: 17.188rem 1fr;
		column-gap: 5rem;

		& > div:last-child {
			justify-self: center;
			width: 100%;
		}
	}

	.add-node-action {
		/* the plus icon uses fill="currentColor", so hovering swaps grey for green*/
		color: var(--color-gray-400);

		&:hover {
			color: var(--primary-green);
		}
	}
</style>
